import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add } from '@mui/icons-material'
import { Box, Button, Card, TableContainer, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useDeleteInternalFeatureModuleMutation,
  useGetInternalFeatureModulesQuery,
  useGetInternalRolesQuery,
  useSaveInternalFeatureModuleMutation,
} from '../../../../api/plans'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { FeatureModule } from '../../types/Plans'
import { ModuleEditDialog } from '../ModuleEditDialog/ModuleEditDialog'
import { formatRoleNames, getUnstoredModuleFields, moduleFieldLabelKeys, ModuleFormValues, toModuleSave } from '../helpers/planHelpers'

type CustomProps = {
  onEdit: (featureModule: FeatureModule) => void
  onDelete: (featureModule: FeatureModule) => void
}

type DialogState = { open: boolean; featureModule?: FeatureModule }

export const ModulesTab: FC = () => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const modulesQuery = useGetInternalFeatureModulesQuery()
  const rolesQuery = useGetInternalRolesQuery()
  const [saveModule, { isLoading: isSaving }] = useSaveInternalFeatureModuleMutation()
  const [deleteModule] = useDeleteInternalFeatureModuleMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<FeatureModule>>()

  const modules = useMemo(() => modulesQuery.data ?? [], [modulesQuery.data])
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])

  const failureMessages = (key: string) => ({ error: t(key), errorWithReason: (reason: string) => `${t(key)}: ${reason}` })

  const tableColumns: GRTableColumn<FeatureModule, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:plans_module_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.name}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.name.toLowerCase(),
        // GRTable compares strings in reverse, so DESC lists names A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-csm:plans_description'),
        accessor: ({ row }) => row.description,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-csm:plans_roles'),
        accessor: ({ row }) => formatRoleNames(row.roles) || t('internal-csm:plans_no_roles'),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 320 } },
      },
      rowActionsColumn<FeatureModule, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-csm:plans_edit_module'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-csm:plans_delete_module'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (featureModule) => setDialogState({ open: true, featureModule }),
      onDelete: (featureModule) =>
        setConfirmDialogData({
          title: t('internal-csm:plans_delete_module'),
          confirmText: t('internal-csm:plans_delete_module_confirm', { name: featureModule.name }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: featureModule,
        }),
    }),
    [t]
  )

  const handleSave = async (values: ModuleFormValues) => {
    const featureModule = toModuleSave(dialogState.featureModule, values)
    const saved = await run(saveModule(featureModule).unwrap(), failureMessages('internal-csm:plans_module_save_failed'))
    if (!saved.ok) return
    const { stored } = saved.result
    if (!stored) {
      notifyError(t('internal-csm:plans_module_not_found_after_save', { name: featureModule.name }))
      return
    }
    const unstored = getUnstoredModuleFields(featureModule, stored)
    if (unstored.length) {
      notifyError(t('internal-csm:plans_not_stored', { fields: unstored.map((field) => t(moduleFieldLabelKeys[field])).join(', ') }))
      return
    }
    notifySuccess(t('internal-csm:plans_module_saved', { name: featureModule.name }))
    setDialogState({ open: false })
  }

  const handleConfirmDelete = async (confirmed?: ConfirmDialogData<FeatureModule>) => {
    setConfirmDialogData(undefined)
    const featureModule = confirmed?.data
    if (!featureModule) return
    const deleted = await run(deleteModule(featureModule.id).unwrap(), failureMessages('internal-csm:plans_module_delete_failed'))
    if (!deleted.ok) return
    if (deleted.result.stored) {
      notifyError(t('internal-csm:plans_module_not_deleted', { name: featureModule.name }))
      return
    }
    notifySuccess(t('internal-csm:plans_module_deleted', { name: featureModule.name }))
  }

  const isLoading = modulesQuery.isLoading || rolesQuery.isLoading
  const isError = modulesQuery.isError || rolesQuery.isError

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {t('internal-csm:plans_modules_help')}
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogState({ open: true })} disabled={isLoading || isError}>
          {t('internal-csm:plans_add_module')}
        </Button>
      </Box>

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-csm:plans_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={modules}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <ModuleEditDialog
        open={dialogState.open}
        featureModule={dialogState.featureModule}
        modules={modules}
        roles={roles}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmDelete} />
    </Box>
  )
}
